import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, ArrowLeft, HelpCircle, Book, Phone, Mail, MessageCircle } from 'lucide-react';

const Help = ({ user, token, language, onLogout, onToggleLanguage }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('faq');

  const translations = {
    en: {
      title: 'Help & Support',
      back: 'Back to Dashboard',
      faq: 'FAQ',
      guides: 'User Guides',
      contact: 'Contact Us',
      
      // FAQ
      q1: 'How do I scan a crop for diseases?',
      a1: 'Go to Dashboard, click "Upload Crop Image", select a photo of your crop, and click "Scan Now". You\'ll get instant results.',
      
      q2: 'How many scans can I do per day?',
      a2: 'Free users get 5 scans per day. Premium users get unlimited scans.',
      
      q3: 'What are the premium features?',
      a3: 'Premium features include: Unlimited scans, Soil Analysis, Weather Forecast, AI Chatbot with voice assistant, and Expert Phone Consultation.',
      
      q4: 'How do I upgrade to premium?',
      a4: 'Click on "Upgrade to Pro" button on dashboard or go to Payment page. Pay ₹499/month via Razorpay.',
      
      q5: 'Can I use the app in Hindi?',
      a5: 'Yes! Click the language toggle button (EN/HI) in the header to switch between English and Hindi.',
      
      q6: 'How do I contact the agricultural expert?',
      a6: 'Premium users can call 9599153699 (Mon-Sat, 9 AM - 6 PM IST) for expert consultation.',
      
      // Guides
      guide1Title: 'Getting Started',
      guide1: '1. Create account with email/mobile\n2. Verify your account\n3. Start with free plan (5 scans/day)\n4. Upload crop images\n5. Get instant disease detection',
      
      guide2Title: 'Using Disease Scanner',
      guide2: '1. Take clear photo of affected crop\n2. Upload on dashboard\n3. Click "Scan Now"\n4. View disease name and treatment\n5. Check scan history anytime',
      
      guide3Title: 'Premium Features',
      guide3: '1. Upgrade to Premium (₹499/month)\n2. Access Soil Analysis\n3. Get Weather Forecasts\n4. Use AI Chatbot with voice\n5. Call expert for advice',
      
      // Contact
      contactEmail: 'Email',
      contactPhone: 'Phone (Premium)',
      contactHours: 'Available: Mon-Sat, 9 AM - 6 PM IST'
    },
    hi: {
      title: 'सहायता और समर्थन',
      back: 'डैशबोर्ड पर वापस जाएं',
      faq: 'सामान्य प्रश्न',
      guides: 'उपयोगकर्ता गाइड',
      contact: 'हमसे संपर्क करें',
      
      q1: 'मैं रोगों के लिए फसल को कैसे स्कैन करूं?',
      a1: 'डैशबोर्ड पर जाएं, "फसल की छवि अपलोड करें" पर क्लिक करें, अपनी फसल की फोटो चुनें, और "अभी स्कैन करें" पर क्लिक करें।',
      
      q2: 'मैं प्रति दिन कितने स्कैन कर सकता हूं?',
      a2: 'मुफ्त उपयोगकर्ताओं को प्रति दिन 5 स्कैन मिलते हैं। प्रीमियम उपयोगकर्ताओं को असीमित स्कैन मिलते हैं।',
      
      q3: 'प्रीमियम सुविधाएं क्या हैं?',
      a3: 'प्रीमियम सुविधाओं में शामिल हैं: असीमित स्कैन, मिट्टी विश्लेषण, मौसम पूर्वानुमान, आवाज सहायक के साथ AI चैटबॉट, और विशेषज्ञ फोन परामर्श।',
      
      q4: 'मैं प्रीमियम में कैसे अपग्रेड करूं?',
      a4: 'डैशबोर्ड पर "प्रो में अपग्रेड करें" बटन पर क्लिक करें या भुगतान पृष्ठ पर जाएं। Razorpay के माध्यम से ₹499/माह का भुगतान करें।',
      
      q5: 'क्या मैं ऐप को हिंदी में उपयोग कर सकता हूं?',
      a5: 'हाँ! अंग्रेजी और हिंदी के बीच स्विच करने के लिए हेडर में भाषा टॉगल बटन (EN/HI) पर क्लिक करें।',
      
      q6: 'मैं कृषि विशेषज्ञ से कैसे संपर्क करूं?',
      a6: 'प्रीमियम उपयोगकर्ता विशेषज्ञ परामर्श के लिए 9599153699 (सोम-शनि, सुबह 9 बजे - शाम 6 बजे IST) पर कॉल कर सकते हैं।',
      
      guide1Title: 'शुरुआत करना',
      guide1: '1. ईमेल/मोबाइल से खाता बनाएं\n2. अपने खाते को सत्यापित करें\n3. मुफ्त योजना से शुरू करें (5 स्कैन/दिन)\n4. फसल की छवियां अपलोड करें\n5. त रोग का पता लगाएं',
      
      guide2Title: 'रोग स्कैनर का उपयोग',
      guide2: '1. प्रभावित फसल की स्पष्ट तस्वीर लें\n2. डैशबोर्ड पर अपलोड करें\n3. "अभी स्कैन करें" पर क्लिक करें\n4. रोग का नाम और उपचार देखें\n5. किसी भी समय स्कैन इतिहास जांचें',
      
      guide3Title: 'प्रीमियम सुविधाएं',
      guide3: '1. प्रीमियम में अपग्रेड करें (₹499/माह)\n2. मिट्टी विश्लेषण तक पहुंचें\n3. मौसम पूर्वानुमान प्राप्त करें\n4. आवाज के साथ AI चैटबॉट का उपयोग करें\n5. सलाह के लिए विशेषज्ञ को कॉल करें',
      
      contactEmail: 'ईमेल',
      contactPhone: 'फोन (प्रीमियम)',
      contactHours: 'उपलब्ध: सोम-शनि, सुबह 9 - शाम 6 बजे IST'
    }
  };

  const t = translations[language];

  const faqs = [
    { q: t.q1, a: t.a1 },
    { q: t.q2, a: t.a2 },
    { q: t.q3, a: t.a3 },
    { q: t.q4, a: t.a4 },
    { q: t.q5, a: t.a5 },
    { q: t.q6, a: t.a6 }
  ];

  const guides = [
    { title: t.guide1Title, content: t.guide1 },
    { title: t.guide2Title, content: t.guide2 },
    { title: t.guide3Title, content: t.guide3 }
  ];

  return (
    <div className="help-page">
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <Leaf size={32} />
            <span>BoAI</span>
          </div>
          <div className="header-actions">
            <button className="lang-toggle" onClick={onToggleLanguage}>
              {language.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="help-content">
        <div className="container">
          <div className="page-header">
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={18} /> {t.back}
            </button>
            <h2>{t.title}</h2>
          </div>

          {/* Help Tabs */}
          <div className="help-tabs">
            <button
              className={activeSection === 'faq' ? 'active' : ''}
              onClick={() => setActiveSection('faq')}
            >
              <HelpCircle size={20} />
              {t.faq}
            </button>
            <button
              className={activeSection === 'guides' ? 'active' : ''}
              onClick={() => setActiveSection('guides')}
            >
              <Book size={20} />
              {t.guides}
            </button>
            <button
              className={activeSection === 'contact' ? 'active' : ''}
              onClick={() => setActiveSection('contact')}
            >
              <MessageCircle size={20} />
              {t.contact}
            </button>
          </div>

          {/* FAQ Section */}
          {activeSection === 'faq' && (
            <div className="help-section">
              {faqs.map((faq, index) => (
                <div key={index} className="faq-card">
                  <h3>{faq.q}</h3>
                  <p>{faq.a}</p>
                </div>
              ))}
            </div>
          )}

          {/* Guides Section */}
          {activeSection === 'guides' && (
            <div className="help-section">
              {guides.map((guide, index) => (
                <div key={index} className="guide-card">
                  <h3>{guide.title}</h3>
                  <pre>{guide.content}</pre>
                </div>
              ))}
            </div>
          )}

          {/* Contact Section */}
          {activeSection === 'contact' && (
            <div className="help-section">
              <div className="contact-card">
                <div className="contact-item">
                  <Mail size={32} />
                  <div>
                    <h4>{t.contactEmail}</h4>
                    <p>support@boai.in</p>
                  </div>
                </div>
                {user?.tier === 'paid' && (
                  <>
                    <div className="contact-item">
                      <Phone size={32} />
                      <div>
                        <h4>{t.contactPhone}</h4>
                        <p>9599153699</p>
                        <p className="contact-hours">{t.contactHours}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Help;
